import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { subscribeQueueEvents } from '../../utils/queueEventBus';
import RankBadge from '../elo/RankBadge';
import CountryFlag from '../common/CountryFlag';
import QueueChat from './QueueChat';
import './Queue.css';

/**
 * Match queue page — list of joined players, join/leave/kick controls.
 *
 * Subscribes to the global queue event bus (fed by useQueueWebSocket at
 * App.js level) instead of opening its own WebSocket. The accept-match
 * modal is rendered globally by App.js / AcceptMatchBanner.
 */
export default function Queue() {
  const { t } = useTranslation();
  const { user, isLoggedIn, getAuthHeaders, login } = useAuth();
  const [queue, setQueue] = useState(null);
  const [config, setConfig] = useState(null);
  const [autoaccept, setAutoaccept] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canKick, setCanKick] = useState(false);
  const [inMatchIds, setInMatchIds] = useState(new Set());
  const [joinBlockMessage, setJoinBlockMessage] = useState(null);

  // Fetch config (default guild)
  useEffect(() => {
    fetch(`${API_URL}/api/web/config`)
      .then(r => r.json())
      .then(data => setConfig(data))
      .catch(console.error);
  }, []);

  const guildId = config?.guild?.guild_id;

  // Check if user has queue.kick permission
  useEffect(() => {
    if (!isLoggedIn) { setCanKick(false); return; }
    fetch(`${API_URL}/api/web/my-permissions`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const cmds = data?.commands || [];
        setCanKick(data?.is_owner || cmds.includes('queue.kick'));
      })
      .catch(() => setCanKick(false));
  }, [isLoggedIn, getAuthHeaders]);

  // Fetch queue status
  const fetchQueue = useCallback(async () => {
    if (!guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/queue/${guildId}`);
      const data = await r.json();
      setQueue(data);
      if (user && data.autoaccept?.includes(user.discord_id)) {
        setAutoaccept(true);
      }
    } catch (err) {
      console.error('Queue fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [guildId, user]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Subscribe to the global queue event bus instead of opening our own WS.
  // useQueueWebSocket (mounted at App.js) owns the single connection and
  // fans every event out via the bus.
  useEffect(() => {
    if (!guildId) return;

    return subscribeQueueEvents((data) => {
      // Chat events are handled by QueueChat; don't refetch the queue list
      // on every chat message — that would hammer the API.
      if (data?.event === 'queue_chat') return;
      // Track players currently in live matches so the queue list can show it
      if (data.event === 'match_live' && data.player_ids) {
        setInMatchIds(prev => new Set([...prev, ...data.player_ids]));
      }
      if (data.event === 'match_ended' && data.player_ids) {
        setInMatchIds(prev => {
          const next = new Set(prev);
          data.player_ids.forEach(id => next.delete(id));
          return next;
        });
      }
      // Refresh the queue list on any event — the backend has already mutated
      // state so a re-fetch is the simplest way to stay in sync.
      fetchQueue();
    });
  }, [guildId, fetchQueue]);

  const handleJoin = async () => {
    if (!isLoggedIn) { login(); return; }
    try {
      const r = await fetch(`${API_URL}/api/queue/${guildId}/join`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      // 403 = registration gate (no Player or no Steam link). The Django
      // backend returns ``{error, code}`` but the FastAPI proxy in stats-page
      // re-wraps non-200 in ``{detail: <body>}``, so we have to dig through
      // the wrapper to find the original ``error`` message.
      if (r.status === 403) {
        const data = await r.json().catch(() => ({}));
        const inner = (data && typeof data.detail === 'object') ? data.detail : data;
        const msg =
          (inner && inner.error)
          || (typeof data.detail === 'string' ? data.detail : null)
          || t('queue.joinBlockedDefault');
        setJoinBlockMessage(msg);
        return;
      }
      fetchQueue();
    } catch (err) {
      console.error('Join error:', err);
    }
  };

  const handleLeave = async () => {
    try {
      await fetch(`${API_URL}/api/queue/${guildId}/leave`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      fetchQueue();
    } catch (err) {
      console.error('Leave error:', err);
    }
  };

  const handleAutoaccept = async () => {
    try {
      const r = await fetch(`${API_URL}/api/queue/${guildId}/autoaccept`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await r.json();
      setAutoaccept(data.autoaccept);
    } catch (err) {
      console.error('Autoaccept error:', err);
    }
  };

  const handleKick = async (targetDiscordId) => {
    try {
      await fetch(`${API_URL}/api/queue/${guildId}/kick`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ discord_id: targetDiscordId }),
      });
      fetchQueue();
    } catch (err) {
      console.error('Kick error:', err);
    }
  };

  const isInQueue = queue?.players?.some(p => p.discord_id === user?.discord_id);

  if (loading) {
    return <div className="queue-loading">{t('queue.loading')}</div>;
  }

  return (
    <div className="queue-page">
      <h1 className="queue-title">{t('queue.title')}</h1>
      {config?.guild && (
        <p className="queue-guild-name">{config.guild.name}</p>
      )}

      <div className="queue-card">
        <div className="queue-header">
          <span className="queue-count">{queue?.count || 0} / {queue?.max_players || 10}</span>
          <span className="queue-status">
            {queue?.locked ? t('queue.matchFound') : t('queue.waitingPlayers')}
          </span>
        </div>

        <div className="queue-players">
          {queue?.players?.map((player, i) => {
            const isInMatch = inMatchIds.has(player.discord_id);
            return (
            <div key={player.discord_id} className={`queue-player${isInMatch ? ' in-match' : ''}`}>
              <span className="queue-player-num">{i + 1}</span>
              <span className={`queue-player-status ${isInMatch ? 'in-match' : 'online'}`}
                    title={isInMatch ? t('queue.inMatch') : t('queue.online')} />
              {player.avatar && (
                <img src={player.avatar} alt="" className="queue-player-avatar" />
              )}
              {player.country && <CountryFlag code={player.country} size="sm" className="queue-player-flag" />}
              <span className="queue-player-name">{player.username}</span>
              {player.elo && (
                <span className="queue-player-elo">
                  <RankBadge rank={player.elo.rank} size="small" />
                  {player.elo.elo}
                </span>
              )}
              {canKick && player.discord_id !== user?.discord_id && (
                <button
                  className="queue-kick-btn"
                  title={t('queue.kickTitle', { name: player.username })}
                  onClick={() => handleKick(player.discord_id)}
                >
                  X
                </button>
              )}
            </div>
          );
          })}

          {Array.from({ length: (queue?.max_players || 10) - (queue?.count || 0) }).map((_, i) => (
            <div key={`empty-${i}`} className="queue-player empty">
              <span className="queue-player-num">{(queue?.count || 0) + i + 1}</span>
              <span className="queue-player-name empty-slot">{t('queue.waitingSlot')}</span>
            </div>
          ))}
        </div>

        <div className="queue-actions">
          {isInQueue ? (
            <button className="queue-btn leave" onClick={handleLeave}>{t('queue.leave')}</button>
          ) : (
            <button className="queue-btn join" onClick={handleJoin}>
              {isLoggedIn ? t('queue.join') : t('queue.loginToJoin')}
            </button>
          )}

          {isLoggedIn && (
            <label className="queue-autoaccept">
              <input
                type="checkbox"
                checked={autoaccept}
                onChange={handleAutoaccept}
              />
              {t('queue.autoAccept')}
            </label>
          )}
        </div>
      </div>

      {guildId && <QueueChat guildId={guildId} />}

      {joinBlockMessage && (
        <div
          className="queue-block-overlay"
          onClick={() => setJoinBlockMessage(null)}
        >
          <div
            className="queue-block-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="queue-block-title">{t('queue.cannotJoinTitle')}</h2>
            <p className="queue-block-message">{joinBlockMessage}</p>
            <button
              className="queue-block-btn"
              onClick={() => setJoinBlockMessage(null)}
            >
              {t('queue.ok')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
